/*
 * pep-free-gift.js
 * ----------------
 * Free-gift automation for a DAWN-based theme.
 *
 * Behavior (mirrors the Horizon reference, adapted to Dawn):
 *   - Keep exactly QTY of the $0 gift product in the cart whenever there is >=1 PAID item.
 *   - Remove the gift when there are no paid items, or when the feature is disabled.
 *   - Show an animated toast when the gift is first added.
 *   - Re-render Dawn's cart sections (drawer / notification / cart page / header bubble)
 *     WITHOUT re-triggering ourselves.
 *
 * Dawn integration notes:
 *   - Dawn has NO DOM `cart:update` event. It uses a pub/sub bus (assets/pubsub.js) with
 *     PUB_SUB_EVENTS.cartUpdate ('cart-update'), published by product-form / cart / quick-add
 *     after every AJAX cart mutation. We subscribe to that and also reconcile on load.
 *   - We tag our own publishes with source 'pep-free-gift' and ignore them to avoid loops.
 *   - After we change the cart we ask Shopify to render the cart sections and swap them into
 *     the DOM so the header bubble, drawer and cart page all reflect the gift immediately.
 */
(function () {
  var G = window.PEP_GIFT || {};
  if (!G.handle || !G.variantId) return;

  var QTY = 1; // 1 free gift per ORDER. For 1 per paid item: set to paidCount in reconcile().
  var SOURCE = 'pep-free-gift';
  var busy = false;

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }
  function getJSON(url) {
    return fetch(root() + url, { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }
  function post(url, body) {
    return fetch(root() + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (d) {
        d.__ok = r.ok; // flag whether the request actually succeeded
        return d;
      });
    });
  }
  function cartJs() {
    return getJSON('cart.js');
  }
  // Remove the gift, retrying if a concurrent/broken cart update caused a failure.
  // Last resort: if it still won't remove and the gift is the ONLY thing left,
  // clear the whole cart to break any corrupted state.
  function removeGift(updates, giftAlone) {
    return post('cart/update.js', { updates: updates }).then(function (d) {
      if (d && d.__ok) return d;
      return new Promise(function (res) { setTimeout(res, 300); }).then(function () {
        return post('cart/update.js', { updates: updates }).then(function (d2) {
          if (d2 && d2.__ok) return d2;
          // Still failing. If the gift is alone, nuke the cart to recover cleanly.
          if (giftAlone) return post('cart/clear.js', {});
          return d2;
        });
      });
    });
  }

  /* ---- Which cart sections to re-render (only those present on the page) ---- */
  function sectionsToRender() {
    var ids = [];
    if (document.getElementById('cart-drawer')) ids.push('cart-drawer');
    if (document.querySelector('cart-drawer')) ids.push('cart-drawer');
    if (document.getElementById('main-cart-items') || document.querySelector('cart-items'))
      ids.push('main-cart-items', 'main-cart-footer');
    if (document.getElementById('cart-icon-bubble') || document.querySelector('#cart-icon-bubble'))
      ids.push('cart-icon-bubble');
    if (document.getElementById('cart-live-region-text')) ids.push('cart-live-region-text');
    if (document.querySelector('cart-notification')) ids.push('cart-notification-product', 'cart-notification-button');
    // de-dupe
    return ids.filter(function (v, i) {
      return ids.indexOf(v) === i;
    });
  }

  /* Fetch fresh server-rendered HTML for the given section ids and swap in the pieces. */
  function refreshCartSections() {
    var ids = sectionsToRender();
    if (!ids.length) return Promise.resolve();
    var url = root() + '?sections=' + encodeURIComponent(ids.join(','));
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        ids.forEach(function (id) {
          var html = sections[id];
          if (html == null) return;
          swapSection(id, html);
        });
      })
      .catch(function () {});
  }

  // Map of section id -> a selector that finds the element to replace inside the page.
  var SECTION_SELECTORS = {
    'cart-icon-bubble': '#cart-icon-bubble',
    'cart-live-region-text': '#cart-live-region-text',
    'main-cart-items': '#main-cart-items',
    'main-cart-footer': '#main-cart-footer',
    'cart-drawer': '#CartDrawer',
  };

  function swapSection(id, html) {
    var selector = SECTION_SELECTORS[id];
    var doc = new DOMParser().parseFromString(html, 'text/html');
    // Prefer a matching inner element from the response; fall back to whole body.
    var incoming = (selector && doc.querySelector(selector)) || doc.querySelector('.shopify-section') || doc.body;
    var target = selector ? document.querySelector(selector) : document.getElementById(id);
    if (target && incoming) {
      target.innerHTML = incoming.innerHTML;
    } else if (selector) {
      // cart-drawer / bubble: try id-based fallback
      var t2 = document.getElementById(id.replace('main-', ''));
      if (t2 && incoming) t2.innerHTML = incoming.innerHTML;
    }
  }

  function announce(cart) {
    // Let Dawn (and our header sync) know the cart changed, tagged so we ignore our own event.
    try {
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cart });
      }
    } catch (e) {}
  }

  var pending = false;
  function reconcile() {
    if (busy) { pending = true; return Promise.resolve(); } // re-run after current pass
    busy = true;
    var added = false;
    var removed = false;
    return cartJs()
      .then(function (cart) {
        var gift = null;
        var paid = 0;
        (cart.items || []).forEach(function (i) {
          if (i.handle === G.handle) gift = i;
          else paid += i.quantity;
        });
        var enabled = G.enabled !== false;
        var want = enabled && paid > 0 ? QTY : 0;
        var have = gift ? gift.quantity : 0;

        // Cart is already in the desired state → clear any reload-loop guard.
        if (have === want) { try { sessionStorage.removeItem('pepReloadedOnce'); } catch (e) {} return null; }
        if (want === 0) {
          if (!gift) return null;
          // Remove the gift by VARIANT ID via cart/update.js (robust against stale line keys).
          // Retry once on failure (concurrent-update race), then let the caller reload.
          var vid = gift.variant_id || G.variantId;
          var upd = {}; upd[vid] = 0;
          removed = true;
          // giftAlone = true when the gift is the only thing in the cart (paid === 0
          // AND no other items) → allows the last-resort cart clear on repeated failure.
          var giftAlone = (cart.items || []).length === 1;
          return removeGift(upd, giftAlone);
        }
        if (!gift && G.available === false) return null; // can't add out-of-stock; don't spam
        if (!gift) {
          added = true;
          return post('cart/add.js', { id: G.variantId, quantity: want });
        }
        return post('cart/change.js', { id: gift.key, quantity: want });
      })
      .then(function (changed) {
        if (!changed) return null;
        if (added && G.showPopup !== false) showToast(G.message);

        // Non-removal changes (adds/quantity): re-render every cart surface in real time.
        if (!removed) {
          return refreshCartSections().then(function () {
            return cartJs().then(function (c) {
              announce(c);          // notify Dawn components + our header sync
              renderDawnCart(c);    // force Dawn's own cart-items/drawer to re-fetch & repaint
              updateHeaderBadge(c); // update the custom header cart count in real time
            });
          });
        }
        return null;
      })
      .catch(function () {})
      .finally(function () {
        busy = false;
        // GIFT REMOVAL on the cart page: reload for a guaranteed-clean state (correct
        // count, empty layout, no stale error banner). This runs even if the removal
        // request errored, so a corrupted cart always self-recovers. Done in finally so
        // it fires regardless of any exception above.
        var onCartPage = !!(document.querySelector('cart-items') || document.getElementById('main-cart-items'));
        if (removed && onCartPage) {
          // Guard against reload loops: only reload once per unresolved removal.
          var flag = 'pepReloadedOnce';
          if (sessionStorage.getItem(flag) !== '1') {
            sessionStorage.setItem(flag, '1');
            window.location.reload();
            return;
          }
        } else {
          try { sessionStorage.removeItem('pepReloadedOnce'); } catch (e) {}
        }
        // Always sync the header badge to the true cart count.
        cartJs().then(updateHeaderBadge).catch(function () {});
        if (pending) { pending = false; reconcile(); } // handle events that arrived mid-pass
      });
  }

  /* Keep the custom header cart-count badge (.ph-header__cart-count) in sync in real time. */
  function updateHeaderBadge(cart) {
    if (!cart) return;
    var count = cart.item_count || 0;
    try {
      // Custom header badge
      document.querySelectorAll('.ph-header__cart-count').forEach(function (badge) {
        if (count > 0) { badge.textContent = count; badge.style.display = ''; }
        else { badge.textContent = ''; badge.style.display = 'none'; }
      });
      // If the custom header has NO badge element yet (count was 0 at page load), create one.
      if (count > 0 && !document.querySelector('.ph-header__cart-count')) {
        var link = document.querySelector('.ph-header__cart');
        if (link) {
          var b = document.createElement('span');
          b.className = 'ph-header__cart-count';
          b.textContent = count;
          link.appendChild(b);
        }
      }
      // Dawn's native bubble, if present
      document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach(function (s) {
        s.textContent = count;
      });
    } catch (e) {}
  }

  /* Force Dawn's native cart components to re-render from the server (real-time, no refresh). */
  function renderDawnCart(cart) {
    try {
      var onCartPage = !!(document.querySelector('cart-items') || document.getElementById('main-cart-items'));
      var count = cart ? (cart.item_count || 0) : null;

      // On the CART PAGE, if we just emptied the cart, Dawn's in-place re-render can't
      // swap in the "empty cart" layout or the footer reliably — reload for a clean state.
      if (onCartPage && count === 0) {
        window.location.reload();
        return;
      }

      // Otherwise re-render Dawn's cart items + footer sections in place.
      document.querySelectorAll('cart-items, cart-drawer-items').forEach(function (c) {
        if (typeof c.onCartUpdate === 'function') c.onCartUpdate();
      });
      // Refresh the cart-page footer (count, subtotal, checkout) which onCartUpdate skips.
      refreshCartFooter();
    } catch (e) {}
  }

  /* Re-render the cart-page footer section (holds the count/subtotal/checkout). */
  function refreshCartFooter() {
    var footer = document.getElementById('main-cart-footer');
    if (!footer) return;
    fetch(root() + '?section_id=main-cart-footer', { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var incoming = doc.getElementById('main-cart-footer') || doc.querySelector('.shopify-section');
        if (incoming) footer.innerHTML = incoming.innerHTML;
      })
      .catch(function () {});
  }

  /* ---------------- Toast ---------------- */
  var toastEl = null;
  var toastTimer = null;
  function buildToast() {
    if (toastEl) return toastEl;
    toastEl = document.createElement('div');
    toastEl.className = 'pep-gift-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    toastEl.innerHTML =
      '<span class="pep-gift-toast__icon" aria-hidden="true">🎁</span>' +
      '<span class="pep-gift-toast__msg"></span>' +
      '<button type="button" class="pep-gift-toast__close" aria-label="Dismiss">&times;</button>';
    toastEl.querySelector('.pep-gift-toast__close').addEventListener('click', hideToast);
    document.body.appendChild(toastEl);
    return toastEl;
  }
  function showToast(msg) {
    var el = buildToast();
    // The toast already shows a 🎁 icon; strip any leading gift emoji from the message
    // so it isn't doubled (regardless of what's set in the theme editor).
    var text = (msg || 'Free gift added to your order!').replace(/^\s*🎁\s*/, '');
    el.querySelector('.pep-gift-toast__msg').textContent = text;
    // force reflow so the transition runs
    void el.offsetWidth;
    el.classList.add('is-visible');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 4500);
  }
  function hideToast() {
    if (toastEl) toastEl.classList.remove('is-visible');
    if (toastTimer) clearTimeout(toastTimer);
  }

  /* ---------------- Wire up ---------------- */
  // Debounce so our reconcile runs AFTER the theme's own cart request settles.
  // Removing/adding a paid line fires cartUpdate while Dawn's cart/change.js is still
  // in flight; issuing our own cart/change.js at the same moment races and Shopify
  // returns "error while updating your cart". Waiting a beat avoids the collision.
  var debounceTimer = null;
  function onCartUpdate(data) {
    if (data && data.source === SOURCE) return; // ignore our own publishes
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, 350);
  }

  function subscribeWhenReady() {
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
      return true;
    }
    return false;
  }

  // pubsub.js is loaded with defer; subscribe as soon as it's available.
  if (!subscribeWhenReady()) {
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      if (subscribeWhenReady() || tries > 50) clearInterval(iv);
    }, 100);
  }

  // Public API: let other scripts (e.g. the custom add-to-cart button) trigger a
  // reconcile directly, so the gift is added/removed in real time without relying
  // on pub/sub event timing. Debounced to coalesce rapid calls.
  window.PEP_reconcile = function () {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, 200);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();

/* ============================================================================
 * GLOBAL add-to-cart interceptor — store-wide.
 * Any "add to cart" (Dawn <product-form> on product pages, featured product,
 * quick-add modals, etc.) adds via AJAX ONLY: shows a popup, updates the count,
 * triggers the free gift, and NEVER navigates to the cart or checkout page.
 * Runs in the capture phase so it beats Dawn's own submit handler.
 * ==========================================================================*/
(function () {
  function root(){ return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/'; }

  function productNameFrom(form){
    // Try the product page title, else the closest card/section title.
    var t = document.querySelector('.product__title, .product__title h1, h1.product__title');
    if (t) return t.textContent.trim();
    var card = form.closest('.card-wrapper, .product-card, li, .grid__item');
    var ct = card && card.querySelector('.card__heading, .card-information__text, .full-unstyled-link, h3');
    return ct ? ct.textContent.trim() : 'Item';
  }

  /* ---- shared toast (reuses the .pep-gift-toast styles) ---- */
  var toastEl=null, toastTimer=null;
  function showToast(msg){
    if(!toastEl){
      toastEl=document.createElement('div');
      toastEl.className='pep-gift-toast ph-add-toast';
      toastEl.setAttribute('role','status');
      toastEl.innerHTML='<span class="pep-gift-toast__icon" aria-hidden="true">🛒</span><span class="pep-gift-toast__msg"></span><button type="button" class="pep-gift-toast__close" aria-label="Dismiss">&times;</button>';
      toastEl.querySelector('.pep-gift-toast__close').addEventListener('click',function(){toastEl.classList.remove('is-visible');});
      document.body.appendChild(toastEl);
    }
    toastEl.querySelector('.pep-gift-toast__msg').textContent=msg;
    toastEl.classList.remove('is-visible'); void toastEl.offsetWidth;
    toastEl.classList.add('is-visible');
    if(toastTimer) clearTimeout(toastTimer);
    toastTimer=setTimeout(function(){toastEl.classList.remove('is-visible');},4500);
  }

  function syncCount(){
    fetch(root()+'cart.js',{headers:{Accept:'application/json'}})
      .then(function(r){return r.json();})
      .then(function(cart){
        var c=cart.item_count||0;
        var badges=document.querySelectorAll('.ph-header__cart-count');
        if(badges.length){ badges.forEach(function(b){ if(c>0){b.textContent=c;b.style.display='';} else {b.remove();} }); }
        else if(c>0){ var link=document.querySelector('.ph-header__cart'); if(link){var s=document.createElement('span');s.className='ph-header__cart-count';s.textContent=c;link.appendChild(s);} }
        document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach(function(s){ s.textContent=c; });
      }).catch(function(){});
  }

  function handleAdd(form){
    var btn = form.querySelector('[type="submit"]');
    var name = productNameFrom(form);
    if(btn){ btn.setAttribute('aria-disabled','true'); btn.classList.add('loading'); }
    var spinner = form.querySelector('.loading__spinner'); if(spinner) spinner.classList.remove('hidden');

    fetch(root()+'cart/add.js',{ method:'POST', headers:{Accept:'application/json'}, body:new FormData(form) })
      .then(function(r){ return r.json().then(function(d){ return {ok:r.ok,data:d}; }); })
      .then(function(res){
        if(res.ok && res.data && res.data.id){
          showToast(name + ' added to cart');
          syncCount();
          try{ if(typeof window.publish==='function' && window.PUB_SUB_EVENTS) window.publish(window.PUB_SUB_EVENTS.cartUpdate,{source:'ph-global-add',cartData:res.data}); }catch(e){}
          try{ if(typeof window.PEP_reconcile==='function') window.PEP_reconcile(); }catch(e){}
        } else {
          var msg=form.querySelector('.product-form__error-message');
          var wrap=form.querySelector('.product-form__error-message-wrapper');
          if(msg && wrap){ msg.textContent=(res.data&&res.data.description)||'Could not add to cart.'; wrap.removeAttribute('hidden'); }
        }
      })
      .catch(function(){})
      .finally(function(){
        if(btn){ btn.removeAttribute('aria-disabled'); btn.classList.remove('loading'); }
        if(spinner) spinner.classList.add('hidden');
      });
  }

  function isAddToCartForm(form){
    if(!form || form.tagName!=='FORM') return false;
    // Dawn add-to-cart forms; exclude localization / customer / search / newsletter forms.
    if(form.getAttribute('data-type')==='add-to-cart-form') return true;
    var action=(form.getAttribute('action')||'');
    return action.indexOf('/cart/add')>-1;
  }

  // Capture-phase submit: beats Dawn's product-form handler → no navigation, no drawer.
  document.addEventListener('submit', function(e){
    var form=e.target;
    if(isAddToCartForm(form)){
      e.preventDefault();
      e.stopImmediatePropagation();
      handleAdd(form);
    }
  }, true);

  // Belt-and-suspenders: also catch the submit button click in capture phase.
  document.addEventListener('click', function(e){
    var btn=e.target.closest('[type="submit"]');
    if(!btn) return;
    var form=btn.closest('form');
    if(isAddToCartForm(form)){
      e.preventDefault();
      e.stopImmediatePropagation();
      handleAdd(form);
    }
  }, true);
})();
